import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const TOPMATE_URL = 'https://topmate.io/acharya_ji_ajay_l_agrawal/2021138?utm_source=public_profile&utm_campaign=acharya_ji_ajay_l_agrawal';

const articles = [
  {
    tag: 'Vedic Astrology',
    tagColor: 'text-amber-800 bg-amber-100 border-amber-300',
    title: 'Why Business Deals Keep Falling Through — A Vedic Astrology Perspective',
    excerpt: 'Most founders blame market conditions. But when the same pattern repeats across different markets and teams, the variable is not external. In Vedic Astrology, the 7th House and the current Dasha period reveal why deals collapse at the last moment.',
    insight: 'Saturn transiting your 7th House creates a friction filter that rejects partnerships not aligned with your karmic path. This is not bad news. It is a quality filter you can learn to work with.',
    cta: 'Book a Dasha Analysis',
    icon: 'Star',
  },
  {
    tag: 'Vastu Shastra',
    tagColor: 'text-emerald-800 bg-emerald-100 border-emerald-300',
    title: 'The North-East Corner of Your Office May Be Costing You Money',
    excerpt: 'Vastu Shastra is not superstition. It is an ancient architectural science rooted in electromagnetic field theory and solar geometry. The North-East zone is the zone of clarity and incoming energy. Blocked or cluttered, it silently suppresses decision-making and cash flow.',
    insight: 'A Vastu audit of a Dubai-based warehouse revealed a water tank placed in the South-East fire zone. Relocating it took 3 days. Revenue recovery began within 6 weeks.',
    cta: 'Book a Vastu Audit',
    icon: 'Home',
  },
  {
    tag: 'Astrology-Led Strategy',
    tagColor: 'text-blue-800 bg-blue-100 border-blue-300',
    title: 'How NRIs Use Planetary Cycles to Time Their Biggest Financial Moves',
    excerpt: "Jupiter's 12-year cycle through the zodiac is the most powerful wealth expansion window in Vedic Astrology. NRI families who understand their personal Jupiter transit can time property acquisitions, business launches, and investment entries with remarkable precision.",
    insight: 'This is not about luck. It is about understanding that the cosmos operates on cycles, and that your personal birth chart is the key to knowing which cycle you are currently inside.',
    cta: 'Analyse My Jupiter Cycle',
    icon: 'TrendingUp',
  },
];

export default function ArticlesBand() {
  const [expanded, setExpanded] = useState(null);

  return (
    <section className="py-28 px-6 md:px-12 bg-brand-light border-y border-stone-200">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8">
          <div>
            <p className="text-brand-gold text-sm uppercase tracking-[0.3em] font-bold mb-4">
              Field Notes from 30 Years of Practice
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-brand-dark leading-tight">
              What the Stars Are <br />
              <span className="italic text-brand-gold">Actually Telling You</span>
            </h2>
          </div>
          <p className="text-stone-600 font-medium text-lg max-w-md leading-relaxed">
            Practical insights on Vedic Astrology, Vastu Shastra, and astrologically-guided decision making. No mysticism. No fluff.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {articles.map((article, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.8 }}
              className="bg-white border border-stone-200 shadow-md rounded-sm p-10 flex flex-col gap-6 hover:border-brand-gold/50 hover:shadow-xl transition-all duration-500 group"
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs uppercase tracking-widest font-bold px-4 py-1.5 rounded-full border ${article.tagColor}`}>
                  {article.tag}
                </span>
                <SafeIcon name={article.icon} className="text-brand-gold text-2xl" />
              </div>
              <h3 className="font-serif text-brand-dark text-2xl leading-snug">{article.title}</h3>
              <p className="text-stone-600 font-medium text-base leading-relaxed flex-grow">{article.excerpt}</p>
              <div
                className="border-l-4 border-brand-gold/40 pl-5 py-2 cursor-pointer bg-brand-accent/30 rounded-r-sm"
                onClick={() => setExpanded(expanded === i ? null : i)}
              >
                <p className="text-brand-dark text-xs uppercase tracking-widest font-bold mb-2 flex items-center justify-between">
                  {expanded === i ? 'Close Insight' : 'Read Key Insight'}
                  <SafeIcon name={expanded === i ? 'ChevronUp' : 'ChevronDown'} className="text-lg" />
                </p>
                {expanded === i && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="text-stone-700 text-base font-medium italic leading-relaxed mt-3"
                  >
                    {article.insight}
                  </motion.p>
                )}
              </div>
              <a
                href={TOPMATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center gap-3 w-full py-4 border-2 border-brand-dark text-brand-dark text-sm font-bold uppercase tracking-widest hover:bg-brand-dark hover:text-white transition-all duration-400 rounded-sm"
              >
                {article.cta}
                <SafeIcon name="ArrowRight" className="text-lg" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}